import React from 'react'
import { amountFormatter, formatDate } from '/src/lib/helpers'
import { formatDisclosedAmount } from '/src/components/ICESections/Shared/helpers'
import { PUBLIC_IMG_URL } from '/src/lib/endpoints'
import Skeleton from 'react-loading-skeleton'

const hasValue = (value) => value !== undefined && value !== null

const ActivityMetrics = ({ entity, user, showInfo, isEntityLoading }) => {
    if (!entity) {
        return null
    }

    const shouldRenderLockedPreview = user?.is_loggedin && !showInfo

    const capitalDeployed = entity?.estimated_deployed_capital
    const numberOfFunds = entity?.number_of_funds
    const totalInvestmentsCount = entity.number_of_investments
    const minTicket = entity?.ticket_size_range?.min
    const maxTicket = entity?.ticket_size_range?.max
    const medianDealSize = entity?.median_deal_size
    const medianValuation = entity?.median_valuation

    const activePortfolioCount = entity?.number_of_active_entities
    const portfolioCompanies = entity?.number_of_entities
    const investmentsTtm = entity?.investments_ttm
    const firstInvestingYearRaw = entity?.first_date_invested


    const formatAmount = (value) => {
        if (!value && value !== 0) {
            return 'N/A'
        }
        return amountFormatter(value)
    }

    const formatDisplay = (
        value,
        { isAmount = false, isDate = false } = {}
    ) => {
        if (value === undefined || value === null) {
            return 'N/A'
        }

        let raw
        if (isDate) {
            raw = formatDate(value)
        } else if (isAmount) {
            raw = formatAmount(value)
        } else {
            raw = value
        }

        return raw
    }

    const renderBlurredImage = (isAmount) => (
        <img
            src={`${PUBLIC_IMG_URL}/blurred_images/blur_mask.png`}
            style={{
                height: '20px',
                width: isAmount ? '60px' : '35px'
            }}
            alt={isAmount ? 'Blurred amount value' : 'Blurred number value'}
            className="highlights__blurred-image"
        />
    )

    let ticketSizeValue = 'N/A'
    let isRangeTicketSize = true
    if (minTicket && maxTicket && (minTicket === maxTicket)) {
        isRangeTicketSize = false
        ticketSizeValue = formatAmount(minTicket)
    } else if (minTicket && maxTicket) {
        // min_ticket_size ~ max_ticket_size
        ticketSizeValue = `${formatAmount(minTicket)} ~ ${formatAmount(maxTicket)}`
    } else if (!maxTicket && minTicket) {
        ticketSizeValue = `Above ${formatAmount(minTicket)}`
    } else if (!minTicket && maxTicket) {
        ticketSizeValue = `Upto ${formatAmount(maxTicket)}`
    }

    let medianValuationDisplay = 'N/A'
    const min = Number(medianValuation?.min)
    const max = Number(medianValuation?.max)

    if (min > 0 && max > 0) {
        medianValuationDisplay = `${formatAmount(min)} ~ ${formatAmount(max)}`
    }

    const hasNoFundsOrInvestments = !numberOfFunds && !totalInvestmentsCount

    const hasCapitalDeployment =
		[
		    capitalDeployed,
		    medianDealSize,
		    minTicket,
		    maxTicket,
		    medianValuation?.min,
		    medianValuation?.max
		].some(hasValue)
    const hasPortfolioActivity =
		[
		    activePortfolioCount,
		    portfolioCompanies,
		    investmentsTtm,
		    firstInvestingYearRaw
		].some(hasValue)

    if (!shouldRenderLockedPreview && !hasCapitalDeployment && !hasPortfolioActivity) {
        if (isEntityLoading) {
            return (
                <div className="activity-metrics">
                    {['Capital Deployment', 'Portfolio Activity'].map(title => (
                        <div key={title} className="activity-metrics__section">
                            <div className="activity-metrics__title">{title}</div>
                            <div className="activity-metrics__grid">
                                {[1, 2, 3, 4].map(n => (
                                    <div key={n}>
                                        <Skeleton width={100} height={13} style={{ marginBottom: 4 }} />
                                        <Skeleton width={64} height={20} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )
        }
        return null
    }

    const capitalDeploymentMetrics = [
        {
            key: 'capital_deployed',
            label: 'Capital Deployed',
            isAmount: true,
            value: formatDisclosedAmount(capitalDeployed, totalInvestmentsCount)
        },
        {
            key: 'ticket_size',
            label: isRangeTicketSize ? 'Typical Ticket Size Range' : 'Ticket Size',
            isAmount: true,
            value: ticketSizeValue
        },
        {
            key: 'median_deal_size',
            label: 'Median Deal Size',
            isAmount: true,
            value: formatDisplay(medianDealSize, { isAmount: true })
        },
        {
            key: 'median_valuation',
            label: 'Median Valuation Range',
            isAmount: true,
            value: medianValuationDisplay
        }
    ]

    const portfolioActivityMetrics = [
        {
            key: 'active_portfolio',
            label: 'Active Portfolio',
            isAmount: false,
            value: formatDisplay(activePortfolioCount)
        },
        {
            key: 'portfolio_companies',
            label: 'Total Companies invested in',
            isAmount: false,
            value: formatDisplay(portfolioCompanies)
        },
        {
            key: 'investments_ttm',
            label: 'Investments TTM',
            isAmount: false,
            value: formatDisplay(investmentsTtm, { isAmount: false })
        },
        {
            key: 'first_investing_year',
            label: 'First Investing Year',
            isAmount: false,
            value: formatDisplay(
                firstInvestingYearRaw
                    ? new Date(firstInvestingYearRaw).getFullYear()
                    : null
            )
        }
    ]

    return (
        <div className="activity-metrics">
            {(shouldRenderLockedPreview || hasCapitalDeployment) && (
                <div
                    className={
                        'profile-section-box activity-metrics__section' +
						(showInfo ? '' : ' paywall-text-blur')
                    }
                >
                    <div className="activity-metrics__title">
						Capital Deployment
                    </div>
                    <div className="activity-metrics__grid">
                        {capitalDeploymentMetrics.map((metric) => (
                            <div key={metric.key} className="activity-metrics__item">
                                <div className="activity-metrics__label">
                                    {metric.label}
                                </div>
                                <div className="activity-metrics__value">
                                    {showInfo ? metric.value : renderBlurredImage(metric.isAmount)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!hasNoFundsOrInvestments && (shouldRenderLockedPreview || hasPortfolioActivity) && (
                <div
                    className={
                        'profile-section-box activity-metrics__section' +
						(showInfo ? '' : ' paywall-text-blur')
                    }
                >
                    <div className="activity-metrics__title">
						Portfolio Activity
                    </div>
                    <div className="activity-metrics__grid">
                        {portfolioActivityMetrics.map((metric) => (
                            <div key={metric.key} className="activity-metrics__item">
                                <div className="activity-metrics__label">
                                    {metric.label}
                                </div>
                                <div className="activity-metrics__value">
                                    {showInfo ? metric.value : renderBlurredImage(metric.isAmount)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ActivityMetrics
